import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { PaginationControls } from '@/shared/ui/Pagination';
import { ROWS_PER_PAGE_OPTIONS } from '../Table.constants';
import type { TablePaginationConfig } from '../Table.types';

/**
 * Rows-per-page picker, the visible range, and the page links. `page` stays
 * zero-based on the way in and out to match the table's own bookkeeping, while
 * `PaginationControls` speaks 1-based pages.
 */
export function TablePaginationBar({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: TablePaginationConfig) {
  const { t } = useTranslation('common');

  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-4 py-2">
      {onRowsPerPageChange && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t('components.tablePaginationBar.rowsPerPage')}
          </span>
          <Select
            value={String(rowsPerPage)}
            onValueChange={(value) => {
              onRowsPerPageChange(Number(value));
            }}
          >
            <SelectTrigger
              size="sm"
              className="w-20"
              aria-label={t('components.tablePaginationBar.rowsPerPage')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROWS_PER_PAGE_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <span className="text-sm text-muted-foreground">
        {/* `total`, not `count` — i18next reads a `count` variable as a plural selector. */}
        {t('components.tablePaginationBar.displayedRows', { from, to, total: count })}
      </span>

      <PaginationControls
        page={page + 1}
        totalItems={count}
        pageSize={rowsPerPage}
        onPageChange={(nextPage) => {
          onPageChange(nextPage - 1);
        }}
      />
    </div>
  );
}
